import { useRef, type ReactNode } from 'react'
import { animate, motion, useMotionValue, useTransform } from 'framer-motion'
import { Trash2 } from 'lucide-react'
import { useIsMobile, usePrefersReducedMotion } from '@/hooks/useMediaQuery'
import { cn } from '@/utils/cn'

const DELETE_OFFSET_PX = 96
const DELETE_VELOCITY = 700

interface SwipeToDeleteProps {
  onDelete: () => void
  children: ReactNode
  className?: string
  /** Surface behind the row while dragging (usually card / list bg). */
  surfaceClassName?: string
  /** Red trash underlay while swiping (inbox rows). Off for reminder pills. */
  revealAffordance?: boolean
  /** Call onDelete immediately (no fly-off) so the next item can appear at once. */
  immediate?: boolean
}

/**
 * Mobile: swipe left or right past a threshold to remove (mail / messages pattern).
 * Desktop: renders children as-is — callers keep an explicit delete control.
 */
export function SwipeToDelete({
  onDelete,
  children,
  className,
  surfaceClassName = 'bg-card',
  revealAffordance = true,
  immediate = false,
}: SwipeToDeleteProps) {
  const isMobile = useIsMobile()
  const reducedMotion = usePrefersReducedMotion()
  const x = useMotionValue(0)
  const underlayOpacity = useTransform(x, [-40, 0, 40], [1, 0, 1])
  const removingRef = useRef(false)
  const dragDistanceRef = useRef(0)

  if (!isMobile) {
    return <div className={cn('relative', className)}>{children}</div>
  }

  const commitDelete = (direction: 1 | -1) => {
    if (removingRef.current) return
    removingRef.current = true

    if (immediate || reducedMotion) {
      // Snap back so the next keyed item never inherits a dragged-away shell /
      // empty underlay. Parent remounts the replacement immediately.
      x.set(0)
      onDelete()
      return
    }

    const target =
      direction * (typeof window !== 'undefined' ? Math.max(window.innerWidth, 360) : 400)

    void animate(x, target, { duration: 0.18, ease: 'easeOut' }).then(() => {
      onDelete()
    })
  }

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {revealAffordance ? (
        <motion.div
          aria-hidden
          style={{ opacity: underlayOpacity }}
          className="pointer-events-none absolute inset-0 flex items-center justify-between bg-destructive px-5"
        >
          <Trash2 className="h-5 w-5 text-destructive-foreground" strokeWidth={2} />
          <Trash2 className="h-5 w-5 text-destructive-foreground" strokeWidth={2} />
        </motion.div>
      ) : null}

      <motion.div
        drag="x"
        dragDirectionLock
        dragElastic={0.12}
        dragConstraints={{ left: -160, right: 160 }}
        style={{ x, touchAction: 'pan-y' }}
        onDragStart={() => {
          dragDistanceRef.current = 0
        }}
        onDrag={(_, info) => {
          dragDistanceRef.current = Math.max(
            dragDistanceRef.current,
            Math.abs(info.offset.x),
          )
        }}
        onDragEnd={(_, info) => {
          if (removingRef.current) return
          const shouldDelete =
            Math.abs(info.offset.x) >= DELETE_OFFSET_PX ||
            Math.abs(info.velocity.x) >= DELETE_VELOCITY
          if (shouldDelete) {
            commitDelete(info.offset.x >= 0 ? 1 : -1)
            return
          }
          void animate(x, 0, { type: 'spring', stiffness: 520, damping: 38 })
        }}
        onClickCapture={(event) => {
          // Don't open the notification after a swipe gesture.
          if (dragDistanceRef.current > 10) {
            event.preventDefault()
            event.stopPropagation()
            dragDistanceRef.current = 0
          }
        }}
        className={cn('relative z-[1] cursor-grab active:cursor-grabbing', surfaceClassName)}
      >
        {children}
      </motion.div>
    </div>
  )
}
